// How the Apps screen orders itself and how it says "when" — the rule shared with iOS
// (`AppRecency.swift`) and Android (`AppRecency.kt`). Keep the three identical: a card that reads
// "2 hours ago" on the phone and "Tuesday" on the desktop for the same open is the same bug as a
// missing feature, and the user meets it every time they switch device.
//
// Pure functions taking `now` and `locale`, for the same reason `date-locale.ts` does: a helper that
// reads the clock or the browser cannot be tested for the boundaries that matter here, and the
// boundaries are the whole rule.

/**
 * Where an app sits in the stream.
 *
 * <p>There is deliberately NO `never` band. A never-opened app sits in `earlier` and says so on its own
 * card ("Never opened"), because a band heading reading "Not opened yet" over a lone app is a question
 * about why it is empty rather than a label — and on a fresh account that heading spans everything.
 * WHERE an app sits and WHAT its card says are two different answers.
 */
export type RecencyBucket = 'today' | 'week' | 'earlier';

/** The order the buckets are rendered in. Exported so the screen cannot invent a different one. */
export const RECENCY_BUCKETS: readonly RecencyBucket[] = ['today', 'week', 'earlier'] as const;

/** Anything at least this old is no longer "this week". Seven days, counted from `now`. */
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function parsed(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : new Date(ms);
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Which band an app belongs to.
 *
 * <p>"Today" is the same CALENDAR day, not the last 24 hours — someone opening an app at 9am wants
 * yesterday's 11pm session under yesterday, not under "today" because it happens to be within the
 * window. The two disagree for most of every morning.
 *
 * <p>A stamp in the FUTURE (clock skew between a device and the server) reads as today rather than
 * being discarded: it is the freshest thing we know about, and hiding it would drop an app the user
 * just opened to the bottom of the screen. That has to be checked BEFORE the calendar-day test, not
 * after — a clock 19 hours fast lands on tomorrow's date, fails "same day", and then trivially passes
 * the elapsed-time test because the elapsed time is NEGATIVE. The card then names a weekday that has
 * not happened yet.
 */
export function recencyBucket(lastOpenedAt: string | undefined, now: Date): RecencyBucket {
  const at = parsed(lastOpenedAt);
  // Never opened sits with the oldest, and the CARD says "Never opened" — see the type note.
  if (!at) return 'earlier';
  if (at.getTime() > now.getTime()) return 'today';
  if (sameLocalDay(at, now)) return 'today';
  if (now.getTime() - at.getTime() < WEEK_MS) return 'week';
  return 'earlier';
}

/**
 * The "when" a card shows, in the reader's own language.
 *
 * <p>Three different shapes on purpose, because the useful answer changes with distance: within the
 * day the gap is what matters ("2 hours ago"), within the week the DAY is what a person remembers
 * ("Tuesday"), and beyond that only the date means anything ("21 Aug"). One relative format for all
 * three would give "6 days ago", which nobody converts back into a day of the week.
 *
 * <p>Returns undefined for a never-opened app: the card shows its own "Never opened" marker instead,
 * and inventing a phrase here would put two different sentences on the same card.
 */
export function relativeOpenedAt(lastOpenedAt: string | undefined, now: Date, locale?: string): string | undefined {
  const at = parsed(lastOpenedAt);
  if (!at) return undefined;
  const bucket = recencyBucket(lastOpenedAt, now);
  try {
    if (bucket === 'today') {
      // `numeric: 'auto'` is what turns 0 into the locale's own "now" rather than "in 0 seconds".
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const deltaMs = at.getTime() - now.getTime();
      const minutes = Math.round(deltaMs / 60000);
      if (Math.abs(minutes) < 60) {
        return rtf.format(minutes, 'minute');
      }
      return rtf.format(Math.round(deltaMs / 3600000), 'hour');
    }
    if (bucket === 'week') {
      return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(at);
    }
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(at);
  } catch {
    // A locale the runtime cannot build a formatter for must not cost the card its whole line, so
    // fall back to the one rendering every runtime can do. Mirrors `localeIsMonthFirst`, which
    // guards its probe the same way.
    //
    // LOCAL date parts, not `toISOString().slice(0,10)` — that is UTC, so west of Greenwich the
    // fallback printed the PREVIOUS day for anything after local midnight.
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  }
}

/** The two fields the ordering reads. Structural, so the comparator does not depend on the full app. */
export interface RecencyOrdered {
  lastOpenedAt?: string;
  createdDate?: string;
  /** Tie-breakers, so the three surfaces cannot disagree when recency and creation both tie. */
  name?: string;
  id?: string;
}

/**
 * Newest-opened first; never-opened last, and among those the newest-BUILT first.
 *
 * <p>The second rule is the one worth stating: sorting never-opened apps by creation puts a
 * just-built app at the top of the group where its owner will look for it, rather than burying it
 * behind everything else they have also never opened.
 *
 * <p>Total and stable — ties fall through to created date, so two apps opened in the same second do
 * not swap places between renders.
 */
export function byRecency(a: RecencyOrdered, b: RecencyOrdered): number {
  const opened = (x: RecencyOrdered) => parsed(x.lastOpenedAt)?.getTime();
  const built = (x: RecencyOrdered) => parsed(x.createdDate)?.getTime() ?? 0;
  const ao = opened(a);
  const bo = opened(b);
  if (ao !== undefined && bo !== undefined && ao !== bo) return bo - ao;
  if (ao !== undefined && bo === undefined) return -1;
  if (ao === undefined && bo !== undefined) return 1;
  if (built(a) !== built(b)) return built(b) - built(a);
  // TOTAL, and identical on iOS (`AppSort.sortedByCreation`) and Android (`compareByCreation`): name
  // case-insensitively, then id. Falling through to `Array.sort` stability instead would order by
  // whatever the server's map iteration produced, which is not a rule the other two surfaces can
  // follow — two apps opened in the same second would sit in a different order on each.
  const byName = (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
  if (byName !== 0) return byName;
  return (a.id ?? '').localeCompare(b.id ?? '');
}

/** The apps of one bucket, already ordered — what a band renders. */
export function inBucket<T extends RecencyOrdered>(apps: readonly T[], bucket: RecencyBucket, now: Date): T[] {
  return apps.filter((app) => recencyBucket(app.lastOpenedAt, now) === bucket).sort(byRecency);
}

/**
 * The single app the "Where you left off" hero shows, or nothing.
 *
 * <p>Nothing when the reader has never opened anything: a hero over an app they have never seen
 * would be answering a question they did not ask, and the phrase would be false.
 */
export function resumeApp<T extends RecencyOrdered>(apps: readonly T[]): T | undefined {
  return [...apps].filter((app) => parsed(app.lastOpenedAt) !== undefined).sort(byRecency)[0];
}

/**
 * Which subtitle key the Apps screen shows — or undefined for no subtitle at all.
 *
 * <p>The subtitle COUNTS ("11 apps your agents built…"), and a count is a claim about the account, so
 * it may only be made once a read has supported it. While loading, or when there is nothing to count,
 * there is no honest number to print: "0 apps your agents built" over a spinner or over a load-failure
 * message is a failed read shipped as an answer. That rule was written down on iOS and implemented
 * nowhere else, which is exactly the kind of divergence the three-surface rule exists to catch — so it
 * is a named function on all three surfaces now (`AppSort.subtitleKey`, `appsSubtitleKey`).
 *
 * <p>Two keys rather than one plural rule: an unresolved i18next plural key renders as the key itself
 * to a reader, and a singular sentence with a plural noun ("1 apps") is copy everybody notices and
 * nobody reports. A ternary can do neither.
 */
export function appsSubtitleKey(shownCount: number, loading: boolean): string | undefined {
  if (loading || shownCount === 0) return undefined;
  return shownCount === 1 ? 'apps.page.descriptionOne' : 'apps.page.descriptionMany';
}
